import { ServicePriceSelectOptionDto } from "akademika/akamont/models/ServicePriceSelectOptionDto";
import { Routes } from "akademika/akamont/constants/Routes";
import {
  UpdateWorkOrderDto,
  WorkOrderItemDto,
} from "akademika/akamont/models/UpdateWorkOrderDto";
import { Button$PressEvent } from "sap/m/Button";
import SeparatorItem from "sap/ui/core/SeparatorItem";
import {
  GetButtonTypeForWorkOrderStatus,
  GetStateForWorkOrderStatus,
  GetTextKeyForWorkOrderStatusButton,
} from "akademika/akamont/utils/WorkOrderStatusUtil";
import JSONModel from "sap/ui/model/json/JSONModel";
import DependentBusinessUnitPartnerDataUtil from "../../utils/DependentBusinessUnitPartnerDataUtil";
import ComboBox, {
  ComboBox$ChangeEvent,
  ComboBox$SelectionChangeEvent,
} from "sap/m/ComboBox";
import {
  ToggleOnlyMyContracts,
  UpdateContractInfoOnChange,
  UpdateContractInfoOnLoad,
} from "akademika/akamont/utils/WorkOrderMainContractUtil";
import { WorkOrderSelectData } from "akademika/akamont/models/WorkOrderSelectData";
import { WorkOrderDetailsInternalDto } from "akademika/akamont/models/WorkOrderDetailsInternalDto";
import Dialog from "sap/m/Dialog";
import { IndicationColor, ValueState } from "sap/ui/core/library";
import MessageType from "sap/ui/core/message/MessageType";
import { WorkOrderStatus } from "../../constants/WorkOrderStatuses";
import { WorkOrderItemActionTypes } from "../../constants/WorkOrderItemActionType";
import {
  ItemInfo,
  UploadSetwithTable$OnActivatedEvent,
  UploadSetwithTable$UploadCompletedEvent,
} from "sap/m/plugins/UploadSetwithTable";
import MessageToast from "sap/m/MessageToast";
import { AttachmentController } from "../sharedControllers/AttachmentController";
import { ChangeWorkOrderStatus } from "akademika/akamont/utils/WorkOrderStatusChangeUtil";
import {
  CheckMainContract,
  CheckWorkOrderItemService,
} from "akademika/akamont/utils/WorkOrderContractCheckUtil";
import { ContextWithPropertySetter } from "akademika/akamont/models/ContextWithPropertySetter";
import { WorkOrderMainContractLogicController } from "../sharedControllers/WorkOrderMainContractLogicController";
import Event from "sap/ui/base/Event";
import { MainContractOptionDto } from "../../models/MainContractOptionDto";
import { IsUserAdmin } from "../../services/AuthService";
import { WorkOrderDetailsWithOptionsDto } from "../../models/WorkOrderDetailsWithOptionsDto";
import MessageBox from "sap/m/MessageBox";

/**
 * @namespace akademika.akamont.pages.workOrderDetailsInternal
 */
export default class WorkOrderDetailsInternal
  extends AttachmentController
  implements WorkOrderMainContractLogicController {
  route = Routes.WORK_ORDER_DETAILS_INTERNAL;
  parentRoute = Routes.WORK_ORDER_LIST;
  navBackUsingHistoryFirst = true;

  _workOrderSelectDataPromise: Promise<WorkOrderSelectData>;

  getWorkOrderDetailsWithOptionsUri =
    "api/WorkOrder/GetWorkOrderDetailsInternalWithOptions";
  addNewWorkOrderItemCommentUri = "api/workOrder/AddWorkOrderItemComment";
  duplicateWorkOrderUri = "api/WorkOrder/DuplicateWorkOrder/{workOrderId}";
  deleteWorkOrderUri = "api/WorkOrder/DeleteWorkOrder/{workOrderId}";

  private _commentDialog: Dialog;
  private _attachmentsDialog: Dialog;

  public async onRouteMatched() {
    this.destroyExistingDialogs();
    this._initInternalModels();

    const workOrderId = this.routeParams.workOrderId;

    this._workOrderSelectDataPromise = {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      then: (onfulfilled: any, onrejected: any) => {
        return this.fetchModel<WorkOrderSelectData>(
          "api/WorkOrder/GetWorkOrderSelectData" + `?workOrderId=${workOrderId}`,
          "workOrderSelectData"
        ).then(onfulfilled, onrejected);
      },
    } as unknown as Promise<WorkOrderSelectData>;

    this.getWorkOrderDetailsWithOptions(workOrderId);
  }

  private destroyExistingDialogs() {
    if (this._commentDialog) {
      this._commentDialog.destroy();
    }
    if (this._attachmentsDialog) {
      this._attachmentsDialog.destroy();
    }
    this._destoryExistingDialogs();
  }

  private async getWorkOrderDetailsWithOptions(workOrderId: string) {
    this.get<WorkOrderDetailsWithOptionsDto>(
      `${this.getWorkOrderDetailsWithOptionsUri}/${workOrderId}`
    )
      .then((data) => {
        this.setModel(
          new JSONModel(data.businessUnitsPartnersSelect),
          "businessUnitsPartnersSelect"
        );
        this.setModel(new JSONModel(data.serviceOptions), "serviceOptions");
        this.setModel(new JSONModel(data.nextStatuses), "nextStatuses");
        this.setModel(new JSONModel(data.auditLog), "auditLog");

        this.setModel(new JSONModel(data.workOrder));
        UpdateContractInfoOnLoad(this, data.workOrder.mainContract);
      })
      .catch((error) => {
        console.error("getWorkOrderDetailsWithOptions:", error);
      });
  }

  private _initInternalModels() {
    this.resetViewOptions();

    if (!this.getModel("businessUnitsPartnersSelect")) {
      this.setModel(
        new JSONModel({ businessUnits: [], partners: [] }),
        "businessUnitsPartnersSelect"
      );
    }
    if (!this.getModel()) {
      this.setModel(new JSONModel({}));
    }

    this.setModel(new JSONModel({ mainContract: {} }));
    this.setModel(new JSONModel(), "workOrderComment");
    this.setModel(new JSONModel({ value: IsUserAdmin() }), "isUserAdmin");
    this.setModel(new JSONModel({ value: true }), "noMainContract");
    this.setModel(new JSONModel([]), "auditLog");
  }

  public initUploadPlugin(event: UploadSetwithTable$OnActivatedEvent) {
    const baseUrl = this.getApiService().getBaseUrl();
    const uploadEndpoint = `${baseUrl}${this.uploadAttachmentUri}`;
    const uploadPlugin = event.getParameter("oPlugin");
    uploadPlugin?.setUploadUrl(uploadEndpoint);
    console.log("Upload plugin initialized.");
  }

  public workOrderAttachmentValidationHandler(itemInfo: ItemInfo) {
    return this.handleAttachmentValidation(itemInfo);
  }

  public onWorkOrderAttachmentUploadCompleted(
    event: UploadSetwithTable$UploadCompletedEvent
  ) {
    this.handleUploadCompleted(event);
  }

  public async openWorkOrderItemAttachments(event: Button$PressEvent) {
    const source = event.getSource();
    const workOrderBindingContext = source.getBindingContext();
    const workOrderItemPath = workOrderBindingContext?.getPath();

    this._attachmentsDialog = await this.openDialog(
      "akademika.akamont.pages.sharedDialogs.AttachmentsDialog",
      true
    );
    this._attachmentsDialog.setModel(source.getModel());
    this._attachmentsDialog.bindElement({ path: workOrderItemPath ?? "" });
  }

  public getStateForWorkOrderStatus = GetStateForWorkOrderStatus;
  public getButtonTypeForWorkOrderStatus = GetButtonTypeForWorkOrderStatus;

  public formatTextForStatusButton(status: string) {
    return this.translate(GetTextKeyForWorkOrderStatusButton(status));
  }

  public priorityHeaderFactory(pricePriority: { key: number }) {
    let priorityTranslationKey;
    switch (pricePriority.key) {
      case 0:
      default:
        priorityTranslationKey = "priorityGeneral";
        break;

      case 1:
        priorityTranslationKey = "priorityBusinessUnit";
        break;

      case 2:
        priorityTranslationKey = "priorityPartner";
        break;

      case 3:
        priorityTranslationKey = "priorityBusinessUnitPartner";
        break;
    }
    return new SeparatorItem({
      text: {
        path: priorityTranslationKey,
        model: "i18n",
      },
    });
  }

  public async refreshWorkOrderDetails() {
    await this.fetchModel(
      "api/WorkOrder/GetNextWorkOrderStatuses/" + this.routeParams.workOrderId,
      "nextStatuses"
    );
    const workOrderDetails = await this.fetchModel<WorkOrderDetailsInternalDto>(
      "api/WorkOrder/GetWorkOrderDetailsForInternal/" +
      this.routeParams.workOrderId
    );
    await this.fetchModel<ServicePriceSelectOptionDto[]>(
      "api/WorkOrder/GetServiceOptionsForInternal",
      "serviceOptions",
      {
        params: {
          businessUnitId: workOrderDetails.businessUnitId,
          partnerId: workOrderDetails.partnerId,
        },
      }
    );
    await this.fetchModel(
      "api/AuditLog/GetWorkOrderLogs/" + this.routeParams.workOrderId,
      "auditLog"
    );

    UpdateContractInfoOnLoad(this, workOrderDetails.mainContract);

    this.resetViewOptions();
    await this.updateBusinessUnitPartnerInputs();

    return workOrderDetails;
  }

  public addWorkOrderItem() {
    const itemList =
      (this.getModel().getProperty("/workOrderItems") as WorkOrderItemDto[]) ||
      [];
    itemList.push({});
    this.getModel().setProperty("/workOrderItems", itemList);
  }

  public removeWorkOrderItem(oEvent: Button$PressEvent) {
    const itemList = this.getModel().getProperty(
      "/workOrderItems"
    ) as WorkOrderItemDto[];

    const workOrderItem = oEvent
      .getSource()
      .getBindingContext()
      ?.getProperty("") as WorkOrderItemDto;

    const index = itemList.indexOf(workOrderItem);
    itemList.splice(index, 1);
    this.getModel().setProperty("/workOrderItems", itemList);
  }

  public async changeStatus(newStatus: WorkOrderStatus) {
    ChangeWorkOrderStatus(
      this,
      newStatus,
      this.refreshWorkOrderDetails.bind(this)
    );
  }

  public async onSaveWorkOrder() {
    await this.whileBusy(async () => {
      this.triggerUploadAttachments();
      MessageToast.show(this.translate("UploadingAttachments"));
      await Promise.all(this._pendingUploadsQueue);
      MessageToast.show(this.translate("AttachmentsUploaded"));
      const workOrderDetails = this.getModelData<UpdateWorkOrderDto>();

      // validate workOrderDetails?
      for (const item of workOrderDetails.workOrderItems) {
        let isItemQuantityValid = item.serviceQuantityCorrected?.toString();
        if (item.isCorrected && !isItemQuantityValid) {
          MessageBox.error(this.translate("correctedQuantityRequired"));
          return;
        }
      }

      await this.post("api/WorkOrder/UpdateWorkOrderForInternal", {
        body: workOrderDetails,
      });
      this.showMessage("saveSuccess");
      await this.refreshWorkOrderDetails();
    })
  }

  public async onEditWorkOrder() {
    const viewOptionsModel = this.getModel("viewOptions") as JSONModel;
    let editValue = viewOptionsModel.getProperty("/edit");
    viewOptionsModel.setProperty("/edit", !editValue);

    if (editValue) {
      await this.refreshWorkOrderDetails();
    }
  }

  public async onCancelEdit() {
    this.getModel("viewOptions").setProperty("/edit", false);
    await this.refreshWorkOrderDetails();
  }

  private resetViewOptions() {
    this.setModel(
      new JSONModel({
        edit: false,
      }),
      "viewOptions"
    );
  }

  public async onCloneWorkOrder() {
    this.openSimpleDialog({
      title: "duplicateWorkOrder",
      message: "duplicateWorkOrderQuestion",
      state: ValueState.Information,
      type: "Confirm",
      confirmWithComment: false,
      confirmAction: async () => {
        const url = this.duplicateWorkOrderUri.replace(
          "{workOrderId}",
          this.routeParams.workOrderId
        );
        const newWorkOrderId = await this.post<number>(url);
        this.navTo(Routes.WORK_ORDER_DETAILS_INTERNAL, {
          workOrderId: newWorkOrderId,
        });
      },
      cancelAction: async () => await this.refreshWorkOrderDetails(),
    });
  }

  public async openWorkOrderItemCommentPopup(workOrderItem: WorkOrderItemDto) {
    const commentModel = new JSONModel({
      newComment: "",
      comments: workOrderItem.comments,
      workOrderItemId: workOrderItem.workOrderItemId,
    });

    this._commentDialog = await this.openDialog(
      "akademika.akamont.pages.workOrderDetailsInternal.WorkOrderItemCommentDialog",
      true
    );
    this._commentDialog.setModel(commentModel, "commentModel");
  }

  public async addWorkOrderItemComment() {
    const newComment = this._commentDialog
      .getModel("commentModel")
      ?.getProperty("/newComment");

    const workOrderItemId = this._commentDialog
      .getModel("commentModel")
      ?.getProperty("/workOrderItemId");

    if (newComment) {
      await this.saveDataAndShowMessage(this.addNewWorkOrderItemCommentUri, {
        params: {
          workOrderItemId,
          newComment,
        },
      });
      this.closeDialog();
      await this.refreshWorkOrderDetails();
    }
  }

  public async addWorkOrderComment() {
    const newComment = this.getModel("workOrderComment").getProperty("/value");
    const workOrderId = this.getModel().getProperty("/workOrderId");

    if (newComment) {
      await this.saveDataAndShowMessage("api/workOrder/AddWorkOrderComment", {
        params: {
          workOrderId: workOrderId,
          newComment,
        },
      });
      await this.refreshWorkOrderDetails();
    }
  }

  public async updateBusinessUnitPartnerInputs() {
    const dependentBusinessUnitPartnerDataUtil =
      new DependentBusinessUnitPartnerDataUtil(this);

    const oModel = this.getModel();

    const businessUnitId = oModel.getProperty("/businessUnitId");
    const partnerId = oModel.getProperty("/partnerId");

    await dependentBusinessUnitPartnerDataUtil.UpdateSelectData(
      businessUnitId,
      partnerId,
      "businessUnitsPartnersSelect"
    );
  }

  public onMainContractSelected() {
    UpdateContractInfoOnChange(this);

    const selectedItemId = (
      this.byId("mainContractId") as ComboBox
    ).getSelectedKey();
    const model = this.getModelData(
      "mainContractOptions"
    ) as MainContractOptionDto[];
    const contract = model.find(
      (mc) => selectedItemId === String(mc.contractId)
    );

    const workOrderId = this.routeParams.workOrderId;

    CheckMainContract(this, contract, workOrderId);
  }

  public updateContractInfoText(event: ComboBox$ChangeEvent) {
    const model = this.getModel();
    model.setProperty("/mainContract/sapId", event.getParameter("value"));
  }

  public disputeWoi(woiId: number) {
    this.openSimpleDialog({
      title: "disputeWorkOrderItem",
      message: "disputeWorkOrderItemQuestion",
      state: ValueState.Error,
      type: "Confirm",
      confirmWithComment: true,
      confirmAction: async (comment?: string) => {
        await this.post(`api/WorkOrder/DisputeWorkOrderItem/${woiId}`, {
          params: {
            newComment: comment,
            actionName: this.translate(WorkOrderItemActionTypes.DISPUTE),
          },
        });
        await this.refreshWorkOrderDetails();
      },
      cancelAction: async () => await this.refreshWorkOrderDetails(),
    });
  }

  public exportToPDF() {
    const workOrderId = this.routeParams.workOrderId;
    this.navTo(Routes.WORK_ORDER_TO_PDF, { workOrderId });
  }

  public async onDeleteWorkOrder() {
    this.openSimpleDialog({
      title: "deleteWorkOrder",
      message: "deleteWorkOrderQuestion",
      state: ValueState.Warning,
      type: "Confirm",
      confirmWithComment: false,
      confirmAction: async () => {
        const uri = this.deleteWorkOrderUri.replace(
          "{workOrderId}",
          this.routeParams.workOrderId
        );
        await this.post(uri);
        this.navTo(this.parentRoute);
      },
      cancelAction: async () => await this.refreshWorkOrderDetails(),
    });
  }

  public onServiceMeasurementUnitSelect(oEvent: ComboBox$SelectionChangeEvent) {
    const selectedItem = oEvent.getParameter("selectedItem");
    if (!selectedItem) return;

    const selectedServiceOption = selectedItem
      ?.getBindingContext("serviceOptions")
      ?.getProperty("");

    const workOrderItemBindingContext =
      selectedItem?.getBindingContext() as ContextWithPropertySetter;

    if (selectedServiceOption.isSingleQuantity) {
      workOrderItemBindingContext.setProperty("serviceQuantityPlanned", 1);
    }
    workOrderItemBindingContext.setProperty(
      "isSingleQuantity",
      selectedServiceOption.isSingleQuantity
    );

    const mainContractId = this.getModel().getProperty(
      "/mainContract/contractId"
    );
    const workOrderId = this.routeParams.workOrderId;
    CheckWorkOrderItemService(
      this,
      mainContractId,
      selectedItem?.getKey(),
      selectedItem?.getText(),
      workOrderId
    );
  }

  public getWorkOrderItemHighlight(
    deviation: string
  ): MessageType | IndicationColor {
    switch (deviation) {
      case "uncompleted":
      case "addedByPartner":
        return MessageType.Error;
      case "orderedQuantityNotPerformed":
        return MessageType.Warning;

      default:
        return MessageType.None;
    }
  }

  public toggleOnlyMyContracts = ToggleOnlyMyContracts;

  public postMainContractControlFilter(oEvent: Event) {
    const body = {
      dispayMainContract:
        this.getModel("noMainContract")?.getProperty("/value") ?? false,
    };
    super.postControlFilter(oEvent, body);
  }
}
